import {ref, type Ref} from 'vue';
import {useRouter} from 'vue-router';
import {useQuasar} from 'quasar';
import {useI18n} from 'vue-i18n';
import {useUserStore} from '@/ts/store/user-store';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {accessCoversProgram} from '@/ts/composables/pure/cdc-dpp-access';
import {pagesPath} from '@/ts/router/pages-path';
import {ContractStatus} from '@/ts/types/dto/user-dto';
import {LicenseProvisionTypeDTO} from '@/ts/types/dto/sign-in-dto';

/**
 * The per-user-type terminal an *eligible* user reaches after choosing "Continue" (no insurance),
 * or after declining/skipping the insurance dialog. Every type lands on a dialog that has its own
 * exit, so the root modal no longer needs an "Exit" button. Each value maps to one screen in
 * {@link cdc-dpp-fallback-dialog.vue}; `null` means no fallback dialog is open.
 * - `UPGRADE_PROMPT` — new/trial: "upgrade to complete the year?" (Upgrade / Leave → offer).
 * - `SUBSCRIBER_CONTINUE` — paying: "continue with your subscription?" (Continue / Leave → offer).
 * - `VOUCHER_GRANT` — voucher too short: "we're giving you a free year" (Continue=grant / Exit).
 * - `COVERED_INFO` — access already covers the programme (long voucher / gift): info (Continue / Exit).
 * - `OFFER_ONE_YEAR` — the free-year save behind "Leave" (Accept=grant / Exit=un-enroll). Terminal:
 *   no return to the root and no separate confirmation, so the paid options are never re-exposed.
 */
export type CdcDppFallbackMode =
  | 'UPGRADE_PROMPT'
  | 'SUBSCRIBER_CONTINUE'
  | 'VOUCHER_GRANT'
  | 'COVERED_INFO'
  | 'OFFER_ONE_YEAR';

/** Reactive state and handlers exposed by {@link useCdcDppFallback}. */
export interface UseCdcDppFallbackReturn {
  fallbackMode: Ref<CdcDppFallbackMode | null>;
  grantingYear: Ref<boolean>;
  runFallback(): void;
  onFallbackGrantYear(): Promise<void>;
  onFallbackSubscriberContinue(): void;
  onFallbackUpgrade(): void;
  onFallbackDeclineToOffer(): void;
  onFallbackContinue(): void;
  onFallbackExit(): Promise<void>;
}

/**
 * Owns the program-completion fallback state machine for an eligible user who is not funding access
 * with insurance. {@link useCdcDppEligibility} drives it via {@link runFallback}, delegates the
 * un-enroll on `onExit`, and is notified via `onResolved` when the user reaches a non-exit terminal
 * (grant / continue / upgrade) so it can mark the eligibility decision acknowledged. The exit path
 * needs no ack: withdrawing ends the enrollment, and a re-enroll starts a fresh one server-side.
 * See docs/insurance-verification.
 */
export function useCdcDppFallback(
  interventionId: Ref<string>,
  onExit: () => Promise<void>,
  onResolved?: () => void,
): UseCdcDppFallbackReturn {
  const api = useApi();
  const router = useRouter();
  const $q = useQuasar();
  const {t} = useI18n();
  const logger = useLogger('useCdcDppFallback');
  const userStore = useUserStore();

  const fallbackMode = ref<CdcDppFallbackMode | null>(null);
  // In-flight guard for the free-year grant: the server is idempotent, but a double-click would
  // still fire duplicate toasts and profile refreshes. Also drives the accept buttons' busy state.
  const grantingYear = ref(false);

  // Routes the eligible, non-insurance user to a per-type terminal (each has its own exit). Paying →
  // "continue subscription?"; access already covers the year → info; voucher-too-short → grant the
  // year; new/trial → "upgrade?".
  function runFallback(): void {
    const contract = userStore.userData?.lastContract;
    const type = contract?.license.provisionType;

    if (type === LicenseProvisionTypeDTO.DIRECT_PAYMENT && contract?.status === ContractStatus.ACTIVE) {
      fallbackMode.value = 'SUBSCRIBER_CONTINUE';
      return;
    }
    if (accessCoversProgram(contract)) {
      fallbackMode.value = 'COVERED_INFO';
      return;
    }
    fallbackMode.value = type === LicenseProvisionTypeDTO.VOUCHER_BASED ? 'VOUCHER_GRANT' : 'UPGRADE_PROMPT';
  }

  // Accepts the stay offer (the free one-year license), then refreshes the profile so
  // paywall/subscription state reflects the swap (the BE cancels a paid subscription as part of
  // the grant). Returns whether the grant succeeded so the caller can gate acknowledgement on it.
  async function grantOneYear(): Promise<boolean> {
    try {
      await api.me.acceptStayOffer(interventionId.value);
      userStore.setUserData(await api.me.getMe());
      $q.notify({type: 'positive', message: t('cdc_dpp_one_year_granted')});
      return true;
    } catch (error) {
      logger.error('Failed to accept the stay offer: {}', error)();
      $q.notify({type: 'negative', message: t('cdc_dpp_one_year_grant_error')});
      return false;
    }
  }

  // Shared accept branch (voucher-too-short auto-grant, and the OFFER_ONE_YEAR accept). Acknowledge
  // and close the dialog ONLY once the grant actually succeeds — acknowledging first would persist
  // the eligibility ack and dismiss the gate even if the grant failed, leaving the user enrolled
  // with no licence and no way back to retry. On failure the dialog stays on its current terminal
  // (the negative toast already fired) so the user can retry the accept.
  async function onFallbackGrantYear(): Promise<void> {
    if (grantingYear.value) {
      return;
    }
    grantingYear.value = true;
    const granted = await grantOneYear();
    // Single-threaded UI guard: the only writer is this function, and re-entry is blocked by the
    // flag itself while the grant is in flight.
    // eslint-disable-next-line require-atomic-updates
    grantingYear.value = false;
    if (!granted) {
      return;
    }
    fallbackMode.value = null;
    onResolved?.();
  }

  // Paying user opted to keep their subscription — nothing to change, continue enrolled.
  function onFallbackSubscriberContinue(): void {
    fallbackMode.value = null;
    onResolved?.();
  }

  // New/trial user opted to upgrade their plan — send them to the existing subscription page where
  // the self-pay purchase flow (plan selection + payment dialog) lives. Deliberately does NOT
  // acknowledge (no onResolved): upgrading is not a settled outcome — the user only navigated to the
  // plans page. If they return without buying, the eligibility gate re-appears so we can still offer
  // the free year (we want their data), rather than silently losing them.
  function onFallbackUpgrade(): void {
    fallbackMode.value = null;
    void router.push(pagesPath.account.subscription);
  }

  // "Leave the program" on the upgrade/subscription prompt → reveal the free-year save as the
  // terminal. Once shown it's Accept-or-Exit; we never bounce back to the paid options.
  function onFallbackDeclineToOffer(): void {
    if (grantingYear.value) {
      return;
    }
    fallbackMode.value = 'OFFER_ONE_YEAR';
  }

  // Access already covers the programme: the user acknowledged the info and continues, nothing to do.
  function onFallbackContinue(): void {
    fallbackMode.value = null;
    onResolved?.();
  }

  // Exit terminal (free-year offer "Exit", or voucher/covered "Exit") → un-enroll and leave. Inert
  // while a grant is in flight: withdrawing then would leave the user un-enrolled AND licensed.
  async function onFallbackExit(): Promise<void> {
    if (grantingYear.value) {
      return;
    }
    fallbackMode.value = null;
    await onExit();
  }

  return {
    fallbackMode,
    grantingYear,
    runFallback,
    onFallbackGrantYear,
    onFallbackSubscriberContinue,
    onFallbackUpgrade,
    onFallbackDeclineToOffer,
    onFallbackContinue,
    onFallbackExit,
  };
}
